"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ArrowRight, FileText, Calendar } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
function FrontlinePostsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-96 w-full animate-pulse bg-muted border border-border" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="mb-4 text-muted-foreground">Kunne ikke indlæse rapporter fra frontlinjen.</p>
        <Button onClick={refetch} variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Prøv igen
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="py-12 text-center border border-dashed border-border">
        <p className="text-muted-foreground font-mono uppercase tracking-widest">Ingen rapporter fundet.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => <Link key={post.id} to={post.link || "#"} className="group block h-full" data-index={i}>
          <Card className="h-full border border-border bg-card transition-all duration-200 ease-out hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px_var(--color-primary)] rounded-none flex flex-col">
            <CardHeader className="space-y-4 p-6 pb-4">
              <div className="flex items-center justify-between border-b border-border pb-2">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground font-sans">
                  <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate>
                  <span>
                    {post.date ? new Date(post.date).toLocaleDateString('da-DK', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric'
                }) : 'UKENDT DATO'}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-primary border border-primary px-1.5 py-0.5">
                  <FileText className="h-3 w-3" />
                  <span>Rapport</span>
                </div>
              </div>
              
              <CardTitle className="font-serif text-2xl font-bold leading-tight tracking-tight text-foreground group-hover:text-primary transition-colors uppercase">
                {post.title}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="p-6 pt-0 flex-grow">
              <div className="line-clamp-4 font-mono text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{
            __html: post.excerpt || ""
          }} />
            </CardContent>
            
            <CardFooter className="p-6 pt-0 mt-auto border-t border-border/50">
              <div className="mt-4 flex w-full items-center justify-between">
                <span className="font-sans text-xs font-bold uppercase tracking-widest text-foreground group-hover:text-primary transition-colors">
                  Læs Rapport
                </span>
                <ArrowRight className="h-4 w-4 text-primary transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </CardFooter>
          </Card>
        </Link>)}
    </div>;
}
export default function SenesteFraFrontlinjen() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="seneste-fra-frontlinjen" className="dark bg-background text-foreground py-20 px-4 md:px-8 border-t-2 border-border">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 border-b-2 border-border pb-6 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-serif text-4xl font-black uppercase tracking-tighter text-foreground md:text-5xl lg:text-6xl">
              <span className="text-primary">Christian Bak:</span> Rapporter fra Frontlinjen
            </h2>
            <p className="mt-4 font-mono text-sm text-muted-foreground max-w-prose">
              Kompromisløs samfundsdebat funderet i militær disciplin og kristen etik. Læs de seneste analyser fra kampen mod tidsåndens hykleri.
            </p>
          </div>
          
          <Link to="/FrontlinePage">
            <Button variant="outline" className="rounded-none border-2 border-foreground bg-transparent px-6 py-6 font-sans text-sm font-bold uppercase tracking-widest text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all shadow-[4px_4px_0px_0px_var(--color-foreground)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]">
              Christian Bak Arkivet
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={wp_query} className="w-full">
          <FrontlinePostsConsumer />
        </PostsProvider>
      </div>
    </section>;
}